import { useMemo } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'

import { useDownloadContext } from '../../DownloadOptionsModal.context'
import {
	DownloadFormValues,
	DownloadOption,
} from '../../DownloadOptionsModal.types'
import { ADD_ALL_MIXES_FIELD } from '../DownloadOptions.constants'
import { getMixDownloadOptions, getOptionName } from '../DownloadOptions.utils'

export type UseMixOptionsSectionReturn = {
	allMixesSelected: boolean
	allMixTypesSelected: boolean
	allMixVariationsSelected: boolean
	canTailorMixOptions: boolean
	handleMixTypesSelectAllClick: () => void
	handleMixVariationsSelectAllClick: () => void
	handleSwitchClick: () => void
	hasMixTypeOptions: boolean
	hasMixVariationOptions: boolean
	mixTypeOptions: DownloadOption[]
	mixVariationOptions: DownloadOption[]
}

const setCategoryOptionValues = (
	optionNames: string[],
	setValue: UseFormSetValue<DownloadFormValues>,
	shouldSelectAll: boolean
): void => {
	optionNames.forEach((optionName) => {
		setValue(optionName, shouldSelectAll)
	})
}

const areAllOptionsSelected = (
	optionNames: string[],
	selectedOptions: Array<boolean | string | undefined>
): boolean =>
	optionNames.length > 0 &&
	selectedOptions.length === optionNames.length &&
	selectedOptions.every((value) => Boolean(value))

export const useMixOptionsSection = (
	control: Control<DownloadFormValues>,
	setValue: UseFormSetValue<DownloadFormValues>
): UseMixOptionsSectionReturn => {
	const { downloadOptions, initialTracks, isLoading } = useDownloadContext()
	const mixOptions: DownloadOption[] = useMemo(
		() => getMixDownloadOptions(downloadOptions?.mixes ?? [], initialTracks),
		[downloadOptions?.mixes, initialTracks]
	)
	const canTailorMixOptions = isLoading || mixOptions.length > 0
	const mixVariationOptions = useMemo(
		() => mixOptions.filter((option) => option.category === 'mixVariations'),
		[mixOptions]
	)
	const mixTypeOptions = useMemo(
		() => mixOptions.filter((option) => option.category === 'mixTypes'),
		[mixOptions]
	)
	const mixVariationOptionNames = useMemo(
		() => mixVariationOptions.map(getOptionName),
		[mixVariationOptions]
	)
	const mixTypeOptionNames = useMemo(
		() => mixTypeOptions.map(getOptionName),
		[mixTypeOptions]
	)
	const allMixOptionNames = useMemo(
		() => mixOptions.map(getOptionName),
		[mixOptions]
	)
	const selectedMixVariationOptions = useWatch({
		control,
		name: mixVariationOptionNames,
	})
	const selectedMixTypeOptions = useWatch({
		control,
		name: mixTypeOptionNames,
	})
	const selectedMixOptions = useWatch({
		control,
		name: allMixOptionNames,
	})
	const selectedUnlabelledMixes = useWatch({
		control,
		name: ADD_ALL_MIXES_FIELD,
	})
	const allMixVariationsSelected = areAllOptionsSelected(
		mixVariationOptionNames,
		selectedMixVariationOptions
	)
	const allMixTypesSelected = areAllOptionsSelected(
		mixTypeOptionNames,
		selectedMixTypeOptions
	)
	const allMixesSelected = canTailorMixOptions
		? areAllOptionsSelected(allMixOptionNames, selectedMixOptions)
		: Boolean(selectedUnlabelledMixes)

	return {
		allMixesSelected,
		allMixTypesSelected,
		allMixVariationsSelected,
		canTailorMixOptions,
		handleMixTypesSelectAllClick: (): void => {
			setCategoryOptionValues(
				mixTypeOptionNames,
				setValue,
				!allMixTypesSelected
			)
		},
		handleMixVariationsSelectAllClick: (): void => {
			setCategoryOptionValues(
				mixVariationOptionNames,
				setValue,
				!allMixVariationsSelected
			)
		},
		handleSwitchClick: (): void => {
			const shouldSelectAll = !allMixesSelected

			if (!canTailorMixOptions) {
				setValue(ADD_ALL_MIXES_FIELD, shouldSelectAll)
				return
			}

			setCategoryOptionValues(allMixOptionNames, setValue, shouldSelectAll)
		},
		hasMixTypeOptions: mixTypeOptions.length > 0,
		hasMixVariationOptions: mixVariationOptions.length > 0,
		mixTypeOptions,
		mixVariationOptions,
	}
}
